/**
 * KinsCircle Phase 6D — Legacy Timeline Engine V1.
 *
 * STABILITY.md §13–§14:
 *   - PURE READ-ONLY orchestration over the existing Phase 6C engines
 *     (`semanticIndexer`, `historianEngine`, `memoryResurfacer`).
 *   - NEVER mutates any memory row, never writes to Supabase, never
 *     persists generated stories.
 *   - Every output block carries `sourceMemoryIds[]`, `memoryCount`,
 *     and `yearRange`, so claims can never be unanchored.
 *   - SAFE-MILESTONE allow-list only. No death/illness/crime/military/
 *     politics/religion/medical inference.
 *   - All builders early-return safely when the master `legacyTimeline`
 *     feature flag is OFF. UI components additionally guard themselves.
 */

import { isLegacyTimelineEnabled } from './featureFlag';
import {
  readIndexedMemories,
  buildMemoryClusters,
} from './semanticIndexer';
import {
  generateFamilyEraSummary,
  findRepeatedFamilyTraditions,
  findMajorLifeMoments,
  buildRelationshipTimeline,
  type FamilyEraSummary,
  type RepeatedTradition,
  type LifeMoment,
  type RelationshipTimelineEntry,
} from './historianEngine';
import type {
  SemanticMemoryRow,
  MemoryCluster,
} from './memoryEmbeddingAdapter';

// ── Types ───────────────────────────────────────────────────────────────────

export type LegacyTimelineSlide = {
  /** Stable id derived from the source memory id (or grouping key). */
  id: string;
  /** Short headline shown over the slide. */
  title: string;
  /** Body copy — drawn from the user's own caption, never invented. */
  body: string;
  /** Optional ISO year. */
  year: number | null;
  /** Optional ISO date string from the source memory. */
  date: string | null;
  /** Original tagged names — first names only. */
  taggedNames: string[];
  /** Soft warm-tone gradient class for the StoryPlaybackModal background. */
  toneGradient: string;
  /** Optional poster image (we never upload — read-only reference). */
  posterUrl?: string | null;
  /** Citation block appended to the slide footer. */
  citation: string;
  /** Always populated — every slide must trace to ≥1 real memory. */
  sourceMemoryIds: string[];
};

export type LegacyTimeline = {
  /** Logical grouping bucket — era / theme / person / overall. */
  bucket: string;
  /** Friendly section label, shown in headers. */
  label: string;
  slides: LegacyTimelineSlide[];
  memoryCount: number;
  yearRange: { from: number | null; to: number | null };
  sourceMemoryIds: string[];
};

export type YearHighlight = {
  year: number;
  memoryCount: number;
  topThemes: string[];
  topPeople: string[];
  sourceMemoryIds: string[];
};

export type MilestoneSequenceEntry = {
  label: string;
  year: number | null;
  date: string | null;
  caption: string;
  taggedNames: string[];
  sourceMemoryIds: string[];
};

export type FamilyJourney = {
  /** A short, factual headline like "From 2008 to 2023, your family shared 142 memories." */
  headline: string;
  /** Top 3 era summaries, in chronological order. */
  eras: FamilyEraSummary[];
  /** Strongest recurring traditions across years. */
  traditions: RepeatedTradition[];
  /** Up to 6 standout milestones (safe-keyword filtered). */
  milestones: LifeMoment[];
  /** Year-by-year activity histogram, oldest → newest. */
  yearHighlights: YearHighlight[];
  /** Theme clusters surfaced from the semantic index. */
  clusters: MemoryCluster[];
  /** Anchored relationship timelines, top 6. */
  relationships: RelationshipTimelineEntry[];
  /** Total memory count and overall span. */
  totals: { memoryCount: number; yearRange: { from: number | null; to: number | null } };
  sourceMemoryIds: string[];
};

export type LegacyBuildOptions = {
  /** Cap slides per timeline. Defaults to 12. */
  maxSlidesPerTimeline?: number;
  /** Cap timelines returned by group helpers. Defaults to 8. */
  maxTimelines?: number;
};

// ── Constants ────────────────────────────────────────────────────────────────

const EMPTY_TIMELINE: LegacyTimeline[] = [];

const TONE_GRADIENTS = [
  'from-amber-200 via-pink-200 to-fuchsia-300',
  'from-rose-200 via-fuchsia-200 to-purple-300',
  'from-sky-200 via-cyan-200 to-emerald-200',
  'from-orange-200 via-amber-200 to-yellow-200',
  'from-indigo-200 via-violet-200 to-fuchsia-300',
  'from-emerald-200 via-teal-200 to-sky-200',
];

const pickGradient = (seed: string): string => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return TONE_GRADIENTS[h % TONE_GRADIENTS.length];
};

// ── Helpers ─────────────────────────────────────────────────────────────────

const eraLabelFromYear = (y: number): string => {
  const decade = Math.floor(y / 10) * 10;
  return `${decade}s`;
};

const trim = (s: string | null | undefined, max: number): string => {
  if (!s) return '';
  const t = s.trim();
  return t.length > max ? `${t.slice(0, max - 1).trim()}…` : t;
};

const headlineFor = (caption: string, taggedNames: string[]): string => {
  if (!caption.trim()) {
    if (taggedNames.length === 1) return `A moment with ${taggedNames[0]}`;
    if (taggedNames.length > 1) return `A moment together`;
    return 'A family moment';
  }
  // First sentence (or first 64 chars).
  const firstSentence = caption.split(/[.!?]\s+/)[0] ?? caption;
  return trim(firstSentence, 64) || 'A family moment';
};

const slideFromMemory = (m: { row: SemanticMemoryRow; year: number | null }): LegacyTimelineSlide => {
  const { row, year } = m;
  const tagged = row.taggedNames ?? [];
  const yearRangeText = year ? `${year}` : 'an undated moment';
  const peopleCount = tagged.length;
  const peopleText = peopleCount
    ? ` featuring ${tagged.slice(0, 3).join(', ')}${peopleCount > 3 ? ` +${peopleCount - 3} more` : ''}`
    : '';
  return {
    id: `slide_${row.id}`,
    title: row.title?.trim() || headlineFor(row.caption ?? '', tagged),
    body: trim(row.caption ?? '', 320) || 'No caption was added to this memory.',
    year,
    date: row.date ?? null,
    taggedNames: tagged,
    toneGradient: pickGradient(row.id),
    posterUrl: null,
    citation: `Based on 1 memory from ${yearRangeText}${peopleText}.`,
    sourceMemoryIds: [row.id],
  };
};

const yearRangeOf = (years: number[]): { from: number | null; to: number | null } => {
  const valid = years.filter((y): y is number => typeof y === 'number');
  if (!valid.length) return { from: null, to: null };
  return { from: Math.min(...valid), to: Math.max(...valid) };
};

// ── Public Builders ─────────────────────────────────────────────────────────

/**
 * Top-level helper. Returns the full chronological timeline + metadata
 * derived purely from the existing local index.
 */
export const buildLegacyTimeline = (
  memories?: { row: SemanticMemoryRow; year?: number | null }[],
  opts: LegacyBuildOptions = {},
): LegacyTimeline | null => {
  if (!isLegacyTimelineEnabled()) return null;

  const max = Math.max(3, Math.min(40, opts.maxSlidesPerTimeline ?? 12));
  const source =
    memories && memories.length
      ? memories.map((m) => ({ row: m.row, year: m.year ?? null }))
      : readIndexedMemories().map((m) => ({ row: m.row, year: m.vector.year }));
  if (!source.length) return null;

  const sorted = [...source].sort((a, b) => {
    if (a.year && b.year) return a.year - b.year;
    if (a.year) return -1;
    if (b.year) return 1;
    return 0;
  });

  const slides = sorted.slice(0, max).map(slideFromMemory);
  const years = sorted.map((s) => s.year).filter((y): y is number => typeof y === 'number');
  return {
    bucket: 'overall',
    label: 'Your Family Story',
    slides,
    memoryCount: sorted.length,
    yearRange: yearRangeOf(years),
    sourceMemoryIds: sorted.map((s) => s.row.id),
  };
};

/** Group every indexed memory by decade era. */
export const groupTimelineByEra = (
  opts: LegacyBuildOptions = {},
): LegacyTimeline[] => {
  if (!isLegacyTimelineEnabled()) return EMPTY_TIMELINE;
  const all = readIndexedMemories();
  if (!all.length) return EMPTY_TIMELINE;

  const buckets: Record<string, { row: SemanticMemoryRow; year: number | null }[]> = {};
  for (const m of all) {
    if (!m.vector.year) continue;
    const key = eraLabelFromYear(m.vector.year);
    if (!buckets[key]) buckets[key] = [];
    buckets[key].push({ row: m.row, year: m.vector.year });
  }
  const max = Math.max(3, Math.min(20, opts.maxSlidesPerTimeline ?? 12));
  const cap = Math.max(1, Math.min(20, opts.maxTimelines ?? 8));

  return Object.keys(buckets)
    .sort()
    .map((era) => {
      const rows = buckets[era].sort(
        (a, b) => (a.year ?? 0) - (b.year ?? 0),
      );
      const ys = rows.map((r) => r.year).filter((y): y is number => typeof y === 'number');
      return {
        bucket: `era:${era}`,
        label: `The ${era}`,
        slides: rows.slice(0, max).map(slideFromMemory),
        memoryCount: rows.length,
        yearRange: yearRangeOf(ys),
        sourceMemoryIds: rows.map((r) => r.row.id),
      };
    })
    .filter((t) => t.memoryCount >= 2)
    .slice(0, cap);
};

/** Group memories by tagged person, ordered by appearance count. */
export const groupTimelineByPerson = (
  opts: LegacyBuildOptions = {},
): LegacyTimeline[] => {
  if (!isLegacyTimelineEnabled()) return EMPTY_TIMELINE;
  const all = readIndexedMemories();
  if (!all.length) return EMPTY_TIMELINE;

  const byPerson: Record<string, { row: SemanticMemoryRow; year: number | null }[]> = {};
  for (const m of all) {
    for (const p of m.vector.people) {
      if (!byPerson[p]) byPerson[p] = [];
      byPerson[p].push({ row: m.row, year: m.vector.year });
    }
  }
  const cap = Math.max(1, Math.min(20, opts.maxTimelines ?? 8));
  const max = Math.max(3, Math.min(20, opts.maxSlidesPerTimeline ?? 12));

  return Object.keys(byPerson)
    .map((p) => {
      const rows = byPerson[p].sort((a, b) => (a.year ?? 0) - (b.year ?? 0));
      const ys = rows.map((r) => r.year).filter((y): y is number => typeof y === 'number');
      return {
        bucket: `person:${p}`,
        label: `Memories with ${p.replace(/\b\w/g, (c) => c.toUpperCase())}`,
        slides: rows.slice(0, max).map(slideFromMemory),
        memoryCount: rows.length,
        yearRange: yearRangeOf(ys),
        sourceMemoryIds: rows.map((r) => r.row.id),
      };
    })
    .filter((t) => t.memoryCount >= 3)
    .sort((a, b) => b.memoryCount - a.memoryCount)
    .slice(0, cap);
};

/** Group memories by semantic theme cluster (Christmas, Cookouts, etc.). */
export const groupTimelineByTheme = (
  opts: LegacyBuildOptions = {},
): LegacyTimeline[] => {
  if (!isLegacyTimelineEnabled()) return EMPTY_TIMELINE;
  const clusters = buildMemoryClusters({ minClusterSize: 3 });
  if (!clusters.length) return EMPTY_TIMELINE;

  const all = readIndexedMemories();
  const byId: Record<string, { row: SemanticMemoryRow; year: number | null }> = {};
  for (const m of all) byId[m.row.id] = { row: m.row, year: m.vector.year };

  const max = Math.max(3, Math.min(20, opts.maxSlidesPerTimeline ?? 12));
  const cap = Math.max(1, Math.min(20, opts.maxTimelines ?? 8));

  return clusters
    .map((c) => {
      const rows = c.memoryIds
        .map((id) => byId[id])
        .filter(Boolean)
        .sort((a, b) => (a!.year ?? 0) - (b!.year ?? 0));
      const ys = rows
        .map((r) => r!.year)
        .filter((y): y is number => typeof y === 'number');
      return {
        bucket: `theme:${c.id}`,
        label: c.label,
        slides: rows.slice(0, max).map((r) => slideFromMemory(r!)),
        memoryCount: rows.length,
        yearRange: { from: c.yearRange.from, to: c.yearRange.to } || yearRangeOf(ys),
        sourceMemoryIds: c.memoryIds.slice(0, max),
      };
    })
    .filter((t) => t.memoryCount >= 3)
    .slice(0, cap);
};

/** Year-by-year histogram of memory activity. */
export const buildYearHighlights = (): YearHighlight[] => {
  if (!isLegacyTimelineEnabled()) return [];
  const all = readIndexedMemories();
  if (!all.length) return [];

  const buckets: Record<
    number,
    {
      ids: string[];
      themes: Record<string, number>;
      people: Record<string, number>;
    }
  > = {};

  for (const { row, vector } of all) {
    if (!vector.year) continue;
    if (!buckets[vector.year]) {
      buckets[vector.year] = { ids: [], themes: {}, people: {} };
    }
    const b = buckets[vector.year];
    b.ids.push(row.id);
    for (const k of Object.keys(vector.terms)) {
      if (k.startsWith('@')) continue;
      b.themes[k] = (b.themes[k] ?? 0) + vector.terms[k];
    }
    for (const p of vector.people) {
      b.people[p] = (b.people[p] ?? 0) + 1;
    }
  }

  return Object.keys(buckets)
    .map((k) => Number(k))
    .sort((a, b) => a - b)
    .map((year) => {
      const b = buckets[year];
      const topThemes = Object.keys(b.themes)
        .sort((a, c) => b.themes[c] - b.themes[a])
        .slice(0, 3);
      const topPeople = Object.keys(b.people)
        .sort((a, c) => b.people[c] - b.people[a])
        .slice(0, 4);
      return {
        year,
        memoryCount: b.ids.length,
        topThemes,
        topPeople,
        sourceMemoryIds: b.ids.slice(0, 12),
      };
    });
};

/** Safe-milestone-only sequence of standout family moments. */
export const buildMilestoneSequence = (
  limit = 8,
): MilestoneSequenceEntry[] => {
  if (!isLegacyTimelineEnabled()) return [];
  const moments = findMajorLifeMoments(limit);
  if (!moments.length) return [];
  const all = readIndexedMemories();
  const byId: Record<string, SemanticMemoryRow> = {};
  for (const m of all) byId[m.row.id] = m.row;

  return moments.map((m) => {
    const row = byId[m.memoryId];
    return {
      label: m.label,
      year: m.year,
      date: row?.date ?? null,
      caption: m.caption || row?.caption || '',
      taggedNames: m.taggedNames,
      sourceMemoryIds: [m.memoryId],
    };
  });
};

/** Top-level family journey payload (used by FamilyDocumentaryCard). */
export const buildFamilyJourney = (): FamilyJourney | null => {
  if (!isLegacyTimelineEnabled()) return null;
  const all = readIndexedMemories();
  if (!all.length) return null;

  const eras = generateFamilyEraSummary().slice(0, 6);
  const traditions = findRepeatedFamilyTraditions().slice(0, 6);
  const milestones = findMajorLifeMoments(6);
  const yearHighlights = buildYearHighlights();
  const clusters = buildMemoryClusters({ minClusterSize: 3 }).slice(0, 6);
  const relationships = buildRelationshipTimeline().slice(0, 6);

  const years = all
    .map((m) => m.vector.year)
    .filter((y): y is number => typeof y === 'number');
  const totals = {
    memoryCount: all.length,
    yearRange: yearRangeOf(years),
  };
  const span =
    totals.yearRange.from && totals.yearRange.to
      ? `From ${totals.yearRange.from} to ${totals.yearRange.to}, `
      : '';
  const headline =
    `${span}your family has shared ${totals.memoryCount} memor${
      totals.memoryCount === 1 ? 'y' : 'ies'
    }.`;

  return {
    headline,
    eras,
    traditions,
    milestones,
    yearHighlights,
    clusters,
    relationships,
    totals,
    sourceMemoryIds: all.map((m) => m.row.id),
  };
};

// Re-export the cluster type so callers don't need a second import.
export type { MemoryCluster };
